import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, Box, Typography, Grid2 } from '@mui/material';
import { theme } from '../../Medical_History_Forms/themeprovider';
import { useLoader } from '../../loaderProvider/loader';
import { infoTextStyle, sectionTitleStyle } from '../../../utils/commonUtils';
import { getEmbryoTransfer,  } from './embryoTransfer.helpers';
import { MasterconfigrutationDetails } from '../oocyteRetrieval/OocyteRetrival.helper';

interface EmbryoTransferDetailsProps {
  cycleId: number | null;
  dropDownOptions?: MasterconfigrutationDetails;
 
}

const EmbryoTransferDetails: React.FC<EmbryoTransferDetailsProps> = ({ cycleId }) => {
  const { t: tGeneral } = useTranslation('general');
  const { t: tSurgery } = useTranslation('surgery_config');
  const { showLoader, hideLoader } = useLoader();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [transferData, setTransferData] = useState<any>(null); // Adjust type as needed

  useEffect(() => {
    if (!cycleId) return;

    showLoader();
    getEmbryoTransfer(cycleId)
      .then((data) => setTransferData(data))
      .catch(() => {
        // setTransferData(defaultEmbryoTransferData); // Default data on error
        setTransferData([]);
      })
      .finally(() => hideLoader());
  }, [cycleId, showLoader, hideLoader]);

  const renderField = (label: string, value: string | number | null | undefined) => {
    if (!value) return null;

    return (
      <Grid2 size={{ xs: 4, sm: 4, md: 4, lg: 3 }}>
        <Typography variant="body1" sx={sectionTitleStyle}>{label}</Typography>
        <Typography variant="body2" sx={infoTextStyle}>{value}</Typography>
      </Grid2>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3 }}>
        <Grid2 container spacing={4} mb={3}>
          {renderField(tSurgery('transferMethod', { defaultValue: 'Transfer Method' }), transferData?.transfer_method_label)}
          {renderField(tGeneral('oocyteFertilized', { defaultValue: 'Oocyte Fertilized' }), transferData?.oocyte_fertilized_method)}
          {renderField(tGeneral('embryoCryopreserved', { defaultValue: 'Embryo Cryopreserved' }), transferData?.embryo_cryo_preservation_method)}
          {renderField(tGeneral('freshTransfer', { defaultValue: 'Fresh Transfer' }), transferData?.fresh_embryo_transfer_method)}
          {renderField(tGeneral('frozenTransfer', { defaultValue: 'Frozen Transfer' }), transferData?.frozen_embryo_transfer_method)}
          {renderField(tGeneral('date', { defaultValue: 'Date' }), transferData?.date)}
          {renderField(tGeneral('startTime', { defaultValue: 'Start Time' }), transferData?.start_time)}
          {renderField(tGeneral('endTime', { defaultValue: 'End Time' }), transferData?.end_time)}
          {renderField(tSurgery('etDishId', { defaultValue: 'ET Dish ID' }), transferData?.et_dish_id)}
          {renderField(tSurgery('embryologist', { defaultValue: 'Embryologist' }), transferData?.embryologist_name)}
          {renderField(tSurgery('witnessEmbryologist', { defaultValue: 'Witness Embryologist' }), transferData?.witness_embryologist_name)}
          {renderField(tSurgery('nurse', { defaultValue: 'Nurse' }), transferData?.nurse_name)}
          {renderField(tSurgery('performedUnderAnesthesia', { defaultValue: 'Performed Under Anesthesia' }), transferData?.performed_under_anesthesia ? tGeneral('yes', { defaultValue: 'Yes' }) : tGeneral('no', { defaultValue: 'No' }))}
          {renderField(tSurgery('anesthesia', { defaultValue: 'Anesthesia' }), transferData?.anesthesia_label)}
          {renderField(tSurgery('anesthetist', { defaultValue: 'Anesthetist' }), transferData?.anesthetist_name)}
          {renderField(tSurgery('endometriumThickness', { defaultValue: 'Endometrium Thickness (mm)' }), transferData?.endometrium_thickness)}
          {renderField(tSurgery('endometriumPattern', { defaultValue: 'Endometrium Pattern' }), transferData?.endometrium_pattern_label)}
          {renderField(tSurgery('ultrasound', { defaultValue: 'Ultrasound' }), transferData?.is_ultrasound ? tGeneral('yes', { defaultValue: 'Yes' }) : tGeneral('no', { defaultValue: 'No' }))}
          {renderField(tSurgery('ultrasoundMethod', { defaultValue: 'Ultrasound Method' }), transferData?.ultrasound_method_label)}
          {renderField(tSurgery('transferDepth', { defaultValue: 'Transfer Depth' }), transferData?.transfer_depth)}
          {renderField(tSurgery('levelOfDifficulty', { defaultValue: 'Level of Difficulty' }), transferData?.difficulty_level_label)}
          {renderField(tSurgery('catheter', { defaultValue: 'Catheter' }), transferData?.catheter_label)}
          {renderField(tSurgery('catheterTube', { defaultValue: 'Catheter Tube' }), transferData?.catheter_tube)}
          {renderField(tSurgery('catheterRemarks', { defaultValue: 'Catheter Remarks' }), transferData?.catheter_remarks)}
          {renderField(tSurgery('noOfAttempts', { defaultValue: 'No of Attempts' }), transferData?.no_of_attempts)}
          {renderField(tSurgery('distanceFromFundus', { defaultValue: 'Distance From Fundus (cm)' }), transferData?.distance_from_fundus)}
          {renderField(tSurgery('embryoRetained', { defaultValue: 'Embryo Retained' }), transferData?.embryo_retained)}
          {renderField(tSurgery('useOfStylet', { defaultValue: 'Use Of Stylet' }), transferData?.is_stylet_used ? tGeneral('yes', { defaultValue: 'Yes' }) : tGeneral('no', { defaultValue: 'No' }))}
          {renderField(tSurgery('useOfTenaculum', { defaultValue: 'Use Of Tenaculum' }), transferData?.is_tenaculum_used ? tGeneral('yes', { defaultValue: 'Yes' }) : tGeneral('no', { defaultValue: 'No' }))}
          {renderField(tSurgery('useOfEmbryoGlue', { defaultValue: 'Use Of Embryo Glue' }), transferData?.is_embryo_glue_used ? tGeneral('yes', { defaultValue: 'Yes' }) : tGeneral('no', { defaultValue: 'No' }))}
          {renderField(tGeneral('complications', { defaultValue: 'Complications' }), transferData?.complications)}
          {renderField(tSurgery('setPregnancyDate', { defaultValue: 'Set Pregnancy Date' }), transferData?.pregnancy_date)}
          {renderField(tSurgery('cycleClose', { defaultValue: 'Cycle Close' }), transferData?.is_cycle_closed ? tGeneral('yes', { defaultValue: 'Yes' }) : tGeneral('no', { defaultValue: 'No' }))}
          {renderField(tSurgery('cycleCloseReason', { defaultValue: 'Cycle Close Reason' }), transferData?.cycle_close_reason)}
          {renderField(tGeneral('notes', { defaultValue: 'Notes' }), transferData?.notes)}
        </Grid2>
      </Box>
    </ThemeProvider>
  );
};

export default EmbryoTransferDetails;
